import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { ZipcodeInput } from '@/components/ZipcodeInput';
import { useLocalStorage } from '@/hooks';
import type { ZipcodeInfo } from '@/types';
import styles from './HomePage.module.css';

const ZIPCODE_STORAGE_KEY = 'intown_zipcode';

const TAGLINES = [
  "What's Brewing in Your Hood?",
  'Catch the Local Buzz',
  "What's Popping Nearby?",
  'The Scoop on Your Block',
  "What's Cooking Around You?",
  'Stay in the Loop, Locally',
  'Uncover Your Neighborhood',
  "Your Area's Main Character Era",
];

export function HomePage() {
  const navigate = useNavigate();
  const [taglineIndex, setTaglineIndex] = useState(() =>
    Math.floor(Math.random() * TAGLINES.length)
  );
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset scroll position on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setTaglineIndex((prev) => {
          let next;
          do {
            next = Math.floor(Math.random() * TAGLINES.length);
          } while (next === prev && TAGLINES.length > 1);
          return next;
        });
        setIsAnimating(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const tagline = TAGLINES[taglineIndex];

  const [savedZipcode, setSavedZipcode] = useLocalStorage(
    ZIPCODE_STORAGE_KEY,
    ''
  );
  const [currentZipcode, setCurrentZipcode] = useState(savedZipcode);
  const [zipcodeInfo, setZipcodeInfo] = useState<ZipcodeInfo | null>(null);
  const [zipcodeError, setZipcodeError] = useState<string>('');

  const handleZipcodeChange = useCallback(
    (zipcode: string, info: ZipcodeInfo | null) => {
      setCurrentZipcode(zipcode);
      setZipcodeInfo(info);
      setZipcodeError('');

      if (info) {
        setSavedZipcode(zipcode);
      }
    },
    [setSavedZipcode]
  );

  const handleSubmit = useCallback(() => {
    if (!currentZipcode) {
      setZipcodeError('Please enter a ZIP code');
      return;
    }

    if (currentZipcode.length !== 5) {
      setZipcodeError('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (!zipcodeInfo) {
      setZipcodeError('Invalid ZIP code. Please enter a valid US ZIP code');
      return;
    }

    setZipcodeError('');
    void navigate(`/feed/${currentZipcode}`);
  }, [currentZipcode, zipcodeInfo, navigate]);

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        <div className={styles.card}>
          <h1 className={`${styles.title} ${isAnimating ? styles.titleFade : ''}`}>
            {tagline}
          </h1>
          <ZipcodeInput
            value={savedZipcode}
            onChange={handleZipcodeChange}
            error={zipcodeError}
          />
          <Button onClick={handleSubmit}>What&apos;s In Town Today</Button>
        </div>
      </main>
    </div>
  );
}
